import type { ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@wa/ui";
import { Pop, useClosePops } from "../shell/Pop";

export interface MenuOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
}

/**
 * A one-of-N choice that opens the CRM's own menu instead of the operating
 * system's.
 *
 * `SelectField` wraps a native `<select>`, which buys keyboard and mobile
 * behaviour for free but draws its popup with the OS: a control sitting between
 * two pills drops a grey system list on top of them. This one is the same `Pop`
 * menu the header and the row actions use, so every dropdown in the app looks
 * like the same piece of software - and `Pop` has since grown the keyboard side
 * that made the native control worth the look (arrows, Home/End, Escape, focus
 * handed back to the trigger).
 *
 * Two dresses, one control. `variant="toolbar"` (the default) is the raised
 * filter pill that sits on the page surface; `variant="form"` is the sunken
 * 52px field that fills its column inside a dialog, next to `crm-inp` inputs.
 * The form one also hangs its panel under the field's left edge at the field's
 * width, because a menu pinned to the right edge of a wide field looks like it
 * belongs to the column next door.
 *
 * Must be rendered inside a `PopHost`, which is also what keeps only one menu on
 * the page open at a time.
 */
export function MenuField<T extends string>({
  value,
  onChange,
  options,
  label,
  variant = "toolbar",
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: MenuOption<T>[];
  /** Accessible name - the trigger only shows the current option. */
  label: string;
  variant?: "toolbar" | "form";
  className?: string;
}) {
  const close = useClosePops();
  const current = options.find((o) => o.value === value) ?? options[0];
  const isForm = variant === "form";

  return (
    <Pop
      matchWidth={isForm}
      trigger={(open) => (
        <button
          type="button"
          className={cn("crm-fld crm-fld-sel", isForm && "crm-fld-form", open && "is-on", className)}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {current?.icon}
          <span className="crm-trunc flex-1 text-left">{current?.label}</span>
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </button>
      )}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={cn("crm-mi", o.value === value && "is-on")}
          onClick={() => {
            onChange(o.value);
            close();
          }}
        >
          {o.icon}
          <span className="flex-1">{o.label}</span>
          {o.value === value ? <Check className="size-4" /> : null}
        </button>
      ))}
    </Pop>
  );
}
